import { Wrench, Zap, Settings, Cable, Plus, RotateCcw, ChevronRight, Phone } from "lucide-react";

/*
 * LocationServices — Section 3/10 for /garage-door-repair-eastern-suburbs/
 * MASTER TEMPLATE — duplicate for other locations, change only the LOC block.
 * Paste this ENTIRE file into src/pages/GarageDoorRepairEasternSuburbs/sections/LocationServices.tsx
 */

// ╔══════════════════════════════════════════════════════════════════╗
// ║  LOC CONFIG — change ONLY this block when duplicating          ║
// ╚══════════════════════════════════════════════════════════════════╝
const LOC = {
  name: "Eastern Suburbs",
  h2Span: "in the Eastern Suburbs",
  intro: "From a stuck spring in Coogee to a motor fault in Vaucluse — we carry parts for every job and fix most faults the same day.",
  ctaLine: "Call for same-day service in the Eastern Suburbs",
  services: [{
    icon: "Wrench",
    title: "Garage Door Repairs",
    desc: "Fast same-day repairs for all door types across the Eastern Suburbs.",
    href: "/garage-door-repair-sydney/",
    tag: "Most Popular"
  }, {
    icon: "Zap",
    title: "Spring Replacement",
    desc: "Broken torsion or extension springs replaced safely, same day.",
    href: "/garage-door-spring-replacement-sydney/",
    tag: null
  }, {
    icon: "Settings",
    title: "Motor & Opener Repair",
    desc: "All brands — B&D, Merlin, ATA, Chamberlain — serviced on-site.",
    href: "/garage-door-motor-repair-sydney/",
    tag: null
  }, {
    icon: "Cable",
    title: "Cable Repair",
    desc: "Snapped cable? We're in the Eastern Suburbs same day, fixed right.",
    href: "/garage-door-cable-repair-sydney/",
    tag: null
  }, {
    icon: "Plus",
    title: "New Door Installation",
    desc: "Supply and fit quality garage doors made to measure for your home.",
    href: "/garage-door-installation-sydney/",
    tag: null
  }, {
    icon: "RotateCcw",
    title: "Roller Door Service",
    desc: "Roller door repairs, panel replacement & motor upgrades.",
    href: "/roller-door-repair-sydney/",
    tag: null
  }]
};
// ╚══════════════════════════════════════════════════════════════════╝

const ICONS: Record<string, any> = {
  Wrench,
  Zap,
  Settings,
  Cable,
  Plus,
  RotateCcw
};
const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
export default function LocationServices() {
  return <section id="location-services" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from LocationIntro bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill="#FAF7F1" />
        </svg>
      </div>

      {/* Grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="ls-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#ls-grain)" />
      </svg>

      {/* Scanlines */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />

      {/* GG watermark */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%,-50%)",
      fontSize: "280px",
      fontWeight: 900,
      color: `${GOLD}08`,
      fontFamily: "Plus Jakarta Sans,sans-serif",
      pointerEvents: "none",
      zIndex: 1,
      userSelect: "none",
      letterSpacing: "-0.05em"
    }}>
        GG
      </div>

      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Header */}
        <div style={{
        maxWidth: "640px",
        marginBottom: "48px"
      }}>
          <div style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "20px"
        }}>
            <div style={{
            width: "32px",
            height: "2px",
            background: GOLD
          }} />
            <span style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: `${GOLD}BB`
          }}>
              Full Service · {LOC.name}
            </span>
          </div>
          <h2 style={{
          fontFamily: "Plus Jakarta Sans,sans-serif",
          fontWeight: 800,
          fontSize: "clamp(26px,3.5vw,42px)",
          lineHeight: 1.1,
          color: "#FFFFFF",
          marginBottom: "16px"
        }}>
            Every Garage Door Problem,<br />
            <span style={{
            color: GOLD
          }}>Fixed {LOC.h2Span}</span>
          </h2>
          <p style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(14px,1.5vw,16px)",
          color: "rgba(255,255,255,0.65)",
          lineHeight: 1.7
        }}>
            {LOC.intro}
          </p>
        </div>

        {/* Service grid */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
        gap: "20px"
      }}>
          {LOC.services.map(({
          icon,
          title,
          desc,
          href,
          tag
        }) => {
          const Icon = ICONS[icon];
          return <a key={title} href={href} style={{
            display: "block",
            background: NAVY2,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "12px",
            padding: "28px",
            textDecoration: "none",
            position: "relative",
            transition: "border-color 0.2s"
          }} className="hover:!border-[#F5A623]/40">
                {tag && <div style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: `${GOLD}20`,
              border: `1px solid ${GOLD}40`,
              borderRadius: "999px",
              padding: "3px 10px",
              fontFamily: "Manrope,sans-serif",
              fontSize: "10px",
              fontWeight: 700,
              color: GOLD,
              letterSpacing: "0.05em"
            }}>
                    {tag}
                  </div>}

                {/** @preserve lucide-do-not-replace */}
                <div data-icon-container="lucide-do-not-replace" style={{
              width: "44px",
              height: "44px",
              borderRadius: "10px",
              background: `${GOLD}18`,
              border: `1px solid ${GOLD}30`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px"
            }}>
                  {Icon && <Icon size={22} color={GOLD} strokeWidth={1.8} />}
                </div>

                <h3 style={{
              fontFamily: "Plus Jakarta Sans,sans-serif",
              fontWeight: 700,
              fontSize: "17px",
              color: "#FFFFFF",
              marginBottom: "8px",
              lineHeight: 1.2
            }}>
                  {title}
                </h3>
                <p style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "13px",
              color: "rgba(255,255,255,0.58)",
              lineHeight: 1.65,
              marginBottom: "16px"
            }}>
                  {desc}
                </p>

                <div style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              color: GOLD,
              fontFamily: "Manrope,sans-serif",
              fontSize: "12px",
              fontWeight: 700
            }}>
                  Learn more
                  {/** @preserve lucide-do-not-replace */}
                  <span data-icon-container="lucide-do-not-replace"><ChevronRight size={13} color={GOLD} strokeWidth={2.5} /></span>
                </div>
              </a>;
        })}
        </div>

        {/* Bottom CTA */}
        <div style={{
        marginTop: "40px",
        textAlign: "center"
      }}>
          <a href="tel:0485000933" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          background: GOLD,
          color: NAVY,
          padding: "14px 32px",
          borderRadius: "8px",
          fontFamily: "Manrope,sans-serif",
          fontWeight: 700,
          fontSize: "15px",
          textDecoration: "none"
        }}>
            {/** @preserve lucide-do-not-replace */}
            <span data-icon-container="lucide-do-not-replace"><Phone size={16} color={NAVY} strokeWidth={2.5} /></span>
            {LOC.ctaLine} — 0485 000 933
          </a>
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in LocationBrands top */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}